/**
 * Theme Toggle Component
 *
 * Provides an accessible button to switch between light, dark, and system themes.
 * Uses lucide-react icons and follows accessibility best practices.
 *
 * @author GitHub Copilot (Theme System - E12)
 */

import React from "react";
import { Sun, Moon, Monitor } from "lucide-react";
import { useTheme } from "../contexts/ThemeContextDefinition.js";
import { THEMES } from "../contexts/theme-utils";
import { Button } from "./ui/button";

/**
 * Get icon component for current theme
 */
function getThemeIcon(theme, effectiveTheme) {
  switch (theme) {
    case THEMES.LIGHT:
      return Sun;
    case THEMES.DARK:
      return Moon;
    case THEMES.SYSTEM:
      return Monitor;
    default:
      return effectiveTheme === "light" ? Sun : Moon;
  }
}

/**
 * Get readable label for theme
 */
function getThemeLabel(theme) {
  switch (theme) {
    case THEMES.LIGHT:
      return "Light mode";
    case THEMES.DARK:
      return "Dark mode";
    case THEMES.SYSTEM:
      return "System theme";
    default:
      return "Toggle theme";
  }
}

/**
 * Get next theme in cycle: light → dark → system → light
 */
function getNextTheme(currentTheme) {
  switch (currentTheme) {
    case THEMES.LIGHT:
      return THEMES.DARK;
    case THEMES.DARK:
      return THEMES.SYSTEM;
    case THEMES.SYSTEM:
      return THEMES.LIGHT;
    default:
      return THEMES.SYSTEM;
  }
}

/**
 * Theme Toggle Button Component
 */
export function ThemeToggle({
  variant = "outline",
  size = "sm",
  showLabel = false,
  className = "",
}) {
  const { theme, effectiveTheme, setTheme } = useTheme();

  const IconComponent = getThemeIcon(theme, effectiveTheme);
  const label = getThemeLabel(theme);

  const handleToggle = () => {
    const nextTheme = getNextTheme(theme);
    setTheme(nextTheme);
  };

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleToggle}
      className={`transition-colors duration-200 ${className}`}
      aria-label={`Switch to next theme (current: ${label})`}
      title={`Current: ${label}. Click to cycle themes.`}
    >
      <IconComponent
        className={`h-4 w-4 transition-transform duration-200 ${
          showLabel ? "mr-2" : ""
        }`}
        aria-hidden="true"
      />
      {showLabel && <span className="text-sm">{label}</span>}
    </Button>
  );
}

/**
 * Simple Binary Theme Toggle (only light/dark)
 * For scenarios where system theme cycling is not desired
 */
export function SimpleThemeToggle({
  variant = "outline",
  size = "sm",
  className = "",
}) {
  const { effectiveTheme, toggleTheme } = useTheme();

  const IconComponent = effectiveTheme === "light" ? Moon : Sun;
  const label =
    effectiveTheme === "light" ? "Switch to dark mode" : "Switch to light mode";

  return (
    <Button
      variant={variant}
      size={size}
      onClick={toggleTheme}
      className={`transition-colors duration-200 ${className}`}
      aria-label={label}
      title={label}
    >
      <IconComponent
        className="h-4 w-4 transition-transform duration-200"
        aria-hidden="true"
      />
    </Button>
  );
}
